/* eslint-disable */
import { useEffect, useRef } from 'react'
import { webSocketWorkerPriceEvent } from '../../../services'
import { routes } from '../../../utils/constants'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { setBloombergSnapshot } from '../../../redux/slices/Bloomberg/bloombergSlice'
import useAxios from '../../../hooks/useAxios'
import { createBloombergCurrency } from '../../../helper'
import MTMCalculator from '../MTMCalculator'
import FeeDollarNotional from '../FeeDollarNotional'
import { matchRoutes } from 'react-router-dom'

const useBloombergSetting = ({
  gridRef,
  priceRatesNormalize,
  setPriceRatesNormalize,
  tradeBlotterResponse,
  tradeBlotterDataAgGrid,
  priceRatesData,
  setPriceRatesData,
  setMenuPriceLoading,
  setBloombergConnection,
  bloombergPriceSnapshot
}) => {
  const dispatch = useDispatch()
  const token = useSelector(state => state.auth.token)
  const { response } = useAxios({ endPoint: routes.currencyPair })
  const { data: currencyPairData } = response

  useEffect(() => {
    if (Array.isArray(currencyPairData) && currencyPairData.length > 0) {
      const brombergSymbol = currencyPairData.map(cp => createBloombergCurrency(cp))
      webSocketWorkerPriceEvent.sendEvent(brombergSymbol)
    }
  }, [currencyPairData])

  const setDataWithLogic = newData => {
    if (newData.type === undefined && newData.security !== 'connection_status') {
      setPriceRatesData(prevData => {
        const index = prevData?.findIndex(price => price.security === newData.security)
        if (index !== -1) {
          if (!isNaN(newData.LAST_PRICE) && newData.LAST_PRICE !== '') {
            if (newData.LAST_PRICE > prevData[index].LAST_PRICE) {
              newData.status = 'UP'
            } else if (newData.LAST_PRICE < prevData[index].LAST_PRICE) {
              newData.status = 'DOWN'
            } else {
              newData.status = prevData[index].status
            }
            const updatedData = [...prevData]
            updatedData[index] = newData
            const { base_currency, counter_currency } = newData
            const symbol = `${base_currency}${counter_currency}`
            setPriceRatesNormalize(prevNormalize => ({
              ...prevNormalize,
              [symbol]: newData
            }))
            return updatedData
          } else {
            return prevData
          }
        } else {
          if (!isNaN(newData.LAST_PRICE) && newData.LAST_PRICE !== '') {
            const { base_currency, counter_currency } = newData
            const symbol = `${base_currency}${counter_currency}`
            setPriceRatesNormalize(prevNormalize => ({
              ...prevNormalize,
              [symbol]: newData
            }))
            return [...prevData, newData]
          } else {
            return prevData
          }
        }
      })
    }
  }

  // Create the calculator instance outside useEffect so it persists
  const mtmCalculator = useRef(MTMCalculator)

  useEffect(() => {
    setTimeout(() => {
      if (gridRef.current && gridRef.current.api && priceRatesNormalize) {
        //console.log('Starting MTM calculations...')

        let calculationsPerformed = 0
        let successfulCalculations = 0

        tradeBlotterDataAgGrid.current.forEach(node => {
          if (
            node.children?.length > 0 &&
            node.staging_status === 'Live' &&
            (node.status === null || node.status === 'NEW')
          ) {
            if (['Spot', 'NDF', 'Forward', 'Swap'].includes(node.type)) {
              let dealTotalMTM = 0
              let validTradeCount = 0
              let tradeResult
              let dollarNotionalResult
              let totalDollarNotional = 0
              let dollarNotionalCount = 0
              let feeResult
              let totalFee = 0
              let feeCount = 0
              node.children.forEach(trade => {
                calculationsPerformed++
                if (node.type !== 'Swap') {
                  tradeResult = mtmCalculator.current.calculateSpotNdfForwardTradeMTM(
                    trade,
                    priceRatesNormalize
                  )
                  dollarNotionalResult = FeeDollarNotional.calculateDollarNotional(
                    trade,
                    priceRatesNormalize
                  )
                } else {
                  //console.log('Calculating MTM for Swap trade:', trade)
                  tradeResult = mtmCalculator.current.calculateSwapTradeMTM(
                    trade,
                    priceRatesNormalize
                  )
                  dollarNotionalResult = FeeDollarNotional.calculateDollarNotionalSwap(
                    trade,
                    priceRatesNormalize
                  )
                }
                // Use the new method that calculates both client and market maker MTM

                if (tradeResult) {
                  trade.pnl_usd_live = tradeResult.total_mtm
                  trade.client_mtm = tradeResult.client_mtm
                  trade.market_maker_mtm = tradeResult.market_maker_mtm
                  trade.last_mtm_update = Date.now()

                  dealTotalMTM += tradeResult.total_mtm
                  validTradeCount++
                  successfulCalculations++
                }

                if (dollarNotionalResult) {
                  trade.dollar_notional = dollarNotionalResult
                  totalDollarNotional += Math.abs(dollarNotionalResult)
                  dollarNotionalCount++

                  // Now we calculate the fees from the dollar notional
                  feeResult = FeeDollarNotional.calculateFee(trade)
                  if (feeResult) {
                    const tradeFee = Math.abs(feeResult)
                    trade.fee = parseFloat(tradeFee).toFixed(2) // Ensure fee is a string with two decimal places
                    totalFee += tradeFee
                    feeCount++
                  }
                }
              })

              // Aggregate MTM at deal level
              if (validTradeCount > 0) {
                node.pnl_usd_live = dealTotalMTM
                node.last_mtm_update = Date.now()
              }
              if (dollarNotionalCount > 0) {
                node.dollar_notional = totalDollarNotional / dollarNotionalCount
              }
              if (feeCount > 0) {
                node.fee = totalFee.toFixed(2) // Ensure fee is a string with two decimal places
              }
            } else if (!['Spot', 'NDF', 'Forward', 'Swap'].includes(node.type)) {
              //node.pnl_usd_live = node.pnl
              let dealTotalPnL = 0
              let validTradeCount = 0
              let tradeResult
              let dollarNotionalResult
              let totalDollarNotional = 0
              let dollarNotionalCount = 0
              let feeResult
              let totalFee = 0
              let feeCount = 0
              node.children.forEach(trade => {
                calculationsPerformed++
                tradeResult = mtmCalculator.current.calculateVanillaExoticTradePnL(
                  trade,
                  priceRatesNormalize
                )
                dollarNotionalResult = FeeDollarNotional.calculateDollarNotional(
                  trade,
                  priceRatesNormalize
                )
                if (tradeResult) {
                  trade.pnl_usd_live = tradeResult.premium
                  trade.last_mtm_update = Date.now()

                  dealTotalPnL += tradeResult.premium
                  validTradeCount++
                  successfulCalculations++
                }
                if (dollarNotionalResult) {
                  trade.dollar_notional = dollarNotionalResult
                  totalDollarNotional += Math.abs(dollarNotionalResult)
                  dollarNotionalCount++

                  // Now we calculate the fees from the dollar notional
                  feeResult = FeeDollarNotional.calculateFee(trade)
                  if (feeResult) {
                    const tradeFee = Math.abs(feeResult)
                    trade.fee = parseFloat(tradeFee).toFixed(2)
                    totalFee += tradeFee
                    feeCount++
                  }
                }
              })
              if (validTradeCount > 0) {
                node.pnl_usd_live = dealTotalPnL
                node.last_mtm_update = Date.now()
              }
              if (dollarNotionalCount > 0) {
                node.dollar_notional = totalDollarNotional / dollarNotionalCount
              }
              if (feeCount > 0) {
                node.fee = totalFee.toFixed(2) // Ensure fee is a string with two decimal places
              }
            }
          }
        })

        // console.log(
        //   `MTM Calculations: ${successfulCalculations}/${calculationsPerformed} successful`
        // )
        gridRef.current.api.refreshServerSide({ purge: false })
      }
    }, 0)
  }, [gridRef.current, priceRatesNormalize, tradeBlotterResponse])

  useEffect(() => {
    const normalizeData = dataArray => {
      return dataArray.reduce((acc, item) => {
        const { base_currency, counter_currency } = item
        const symbol = `${base_currency}${counter_currency}`
        acc[symbol] = item
        return acc
      }, {})
    }

    const handleConnectionStatus = status => {
      setBloombergConnection(status)
      if (!status) {
        setPriceRatesData(prev =>
          prev.length > 0 ? prev.map(({ status, ...rest }) => rest) : prev
        )
      }
    }

    const handleSnapshotUpdate = async () => {
      const url = `${process.env.REACT_APP_SERVER_URL}${routes.currencyPrice}`
      const result = await axios.get(url, { headers: { Authorization: token } })
      setPriceRatesData(result.data)
      setPriceRatesNormalize(normalizeData(result.data))
      dispatch(setBloombergSnapshot(result.data))
      setMenuPriceLoading(false)
    }

    const handelMessage = async ({ data, type }) => {
      if (!data || type === 'RPC') return

      const { security, status } = data

      if (security === 'connection_status') {
        handleConnectionStatus(status)
        if (status && bloombergPriceSnapshot.length > 0 && priceRatesData.length === 0) {
          setPriceRatesData(bloombergPriceSnapshot)
          setPriceRatesNormalize(normalizeData(bloombergPriceSnapshot))
          setMenuPriceLoading(false)
        }
        return
      }

      if (security === 'bloomberg_snapshot') {
        await handleSnapshotUpdate()
        return
      }

      // Streaming price update
      setDataWithLogic(data)
      setMenuPriceLoading(false)

      // If snapshot was stored earlier, use it to normalize
      if (bloombergPriceSnapshot.length > 0) {
        setPriceRatesData(bloombergPriceSnapshot)
        setPriceRatesNormalize(normalizeData(bloombergPriceSnapshot))
        setMenuPriceLoading(false)
      }
    }

    webSocketWorkerPriceEvent.addEventListener('message', handelMessage)

    return () => {
      webSocketWorkerPriceEvent.removeEventListener('message', handelMessage)
    }
  }, [webSocketWorkerPriceEvent, bloombergPriceSnapshot, currencyPairData])
}

export default useBloombergSetting
